import React from 'react';
import PropTypes from 'prop-types';
import { useHistory, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import Button from 'components/ui/Button';
import { Row } from 'components/shared/Layout';
import { codes as paymentCodes } from 'constants/payment';
import { compensationsShape } from 'constants/propTypes';
import { REFUND_25, REFUND_50, REFUND_75 } from 'constants/prrRefund';

import {
  getTotalPoints,
  getRefundAmount,
  getMembershipNumberInBooking,
  isValidMembershipNumber,
  getRefundOptions
} from './PrrRefund.utils';
import RefundAmountSelection from './RefundAmountSelection';
import { StyledP, Error } from './PrrRefund.styles';
import CompensationBreakdown from './CompensationBreakdown/CompensationBreakdown';

const renderLoyaltyRefundDetails = (validMembershipNumber, membershipNumberInBooking = null, location) => {
  if (validMembershipNumber) {
    return <FormattedMessage id="prrRefund.refundEachLoyaltyAccountRules" />;
  }

  return !membershipNumberInBooking ? (
    <Error id="error-not-loyalty-account">
      <FormattedMessage
        id="errorBoundary.notLoggedInLoyalty"
        values={{
          here: (
            <Link
              to={{
                pathname: '/clubeurostar/manage',
                state: {
                  prevPath: location.pathname,
                  prevSearch: location.search,
                  prevHash: location.hash
                }
              }}
            >
              <FormattedMessage id="errorBoundary.linkHere" />
            </Link>
          )
        }}
      />
    </Error>
  ) : (
    <Error id="error-not-loyalty-account">
      <FormattedMessage
        id="errorBoundary.mismatchedLoyalty"
        values={{
          here: (
            <Link
              to={{
                pathname: '/clubeurostar/manage',
                state: {
                  prevPath: location.pathname,
                  prevSearch: location.search,
                  prevHash: location.hash
                }
              }}
            >
              <FormattedMessage id="errorBoundary.linkHere" />
            </Link>
          )
        }}
      />
    </Error>
  );
};

const PrrRefundLoyalty = ({
  onSubmit,
  reference,
  filteredProducts,
  compensateLegs,
  compensationItems,
  loading,
  itemRefs,
  membershipNumber,
  additionalDetails = [],
  username,
  isCashTicketRefund = false,
  currency = '',
  isLoyaltyBooking = false
}) => {
  const { location } = useHistory();

  const serviceTypes = compensateLegs.map((leg) => leg.serviceType);
  const refundOptions = getRefundOptions(
    compensationItems,
    serviceTypes,
    [REFUND_25, REFUND_50, REFUND_75],
    false,
    true,
    isCashTicketRefund
  );

  const {
    register,
    handleSubmit,
    formState: { isValid },
    watch
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      refundPercentage: String(refundOptions?.[0]?.value ?? REFUND_25.value)
    }
  });

  const refundPercentage = watch('refundPercentage');
  const totalPoints = getTotalPoints(filteredProducts);
  const membershipNumberInBooking = getMembershipNumberInBooking(additionalDetails);
  const validMembershipNumber = isValidMembershipNumber(membershipNumberInBooking, membershipNumber);
  const refundAmount = Math.ceil(getRefundAmount(totalPoints, refundPercentage));
  const membershipNumberToDisplay = membershipNumberInBooking || membershipNumber;

  const onSubmitForm = () => {
    const variables = [];

    if (compensationItems.length > 0) {
      compensationItems.forEach((compensation) => {
        const membershipNumber = compensation.membershipNumber;

        const refundRecord = variables.find(({ membershipNumber: refundMembershipNumber }) => refundMembershipNumber === membershipNumber);

        if (refundRecord) {
          const product = filteredProducts.find(({ ticketNumber }) => ticketNumber === compensation.ticketNumber);

          refundRecord.refundAmount += isLoyaltyBooking ? compensation.loyaltyCompensationAmount : compensation.onlinePointsAmount;
          refundRecord.additionalDetails.push({
            itemRefId: Number(product.itemRef),
            key: `PRR_Compensation_${refundPercentage}%`,
            value: `${isLoyaltyBooking ? String(compensation.loyaltyCompensationAmount) : String(compensation.onlinePointsAmount)} points`
          });
          refundRecord.compensationRecordsToUpdate.push({
            compensationRecordId: compensation.compensationRecordId
          });
        } else {
          const product = filteredProducts.find(({ ticketNumber }) => ticketNumber === compensation.ticketNumber);

          variables.push({
            bookingReference: reference,
            username,
            membershipNumber,
            refundAmount: isLoyaltyBooking ? compensation.loyaltyCompensationAmount : compensation.onlinePointsAmount,
            additionalDetails: [
              {
                itemRefId: Number(product.itemRef),
                key: `PRR_Compensation_${refundPercentage}%`,
                value: `${
                  isLoyaltyBooking ? String(compensation.loyaltyCompensationAmount) : String(compensation.onlinePointsAmount)
                } points`
              }
            ],
            compensationRecordsToUpdate: [
              {
                compensationRecordId: compensation.compensationRecordId,
                caseId: compensation.caseId
              }
            ]
          });
        }
      });
    } else {
      variables.push({
        bookingReference: reference,
        refundAmount: Number(refundAmount),
        additionalDetails: itemRefs.map((ref) => {
          const product = filteredProducts.find(({ itemRef }) => itemRef === ref);

          return {
            itemRefId: Number(ref),
            key: `PRR_Compensation_${refundPercentage}%`,
            value: `${String(getRefundAmount(product?.loyaltyPoints ?? 0, refundPercentage))} points`
          };
        }),
        membershipNumber: membershipNumberToDisplay,
        username
      });
    }

    onSubmit({ method: paymentCodes.LOYALTY_POINTS, variables });
  };

  const isDisabled = isCashTicketRefund ? false : !isValid || loading || !validMembershipNumber;

  return (
    <div>
      <h2>
        <FormattedMessage id="prrRefund.compensationAmount" />
      </h2>

      <Row>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <Row>
            <RefundAmountSelection options={refundOptions} register={register} />
          </Row>
          <h2>
            <FormattedMessage id="prrRefund.compensationTickets" />
          </h2>
          <CompensationBreakdown
            isLoyalty
            isCashTicketRefund={isCashTicketRefund}
            currency={currency}
            compensateLegs={compensateLegs}
            refundPercentage={refundPercentage}
            compensationItems={compensationItems}
          />
          <Row>
            <h2>
              <FormattedMessage id="prrRefund.processRefund" />
            </h2>
            {!isCashTicketRefund && (
              <StyledP>{renderLoyaltyRefundDetails(validMembershipNumber, membershipNumberInBooking, location)}</StyledP>
            )}
          </Row>

          <Button data-cy="submit-prr-refund" type="submit" disabled={isDisabled} loading={loading}>
            <FormattedMessage id="prrRefund.processRefundCta" />
          </Button>
        </form>
      </Row>
    </div>
  );
};

PrrRefundLoyalty.propTypes = {
  username: PropTypes.string.isRequired,
  filteredProducts: PropTypes.arrayOf(
    PropTypes.shape({
      itemRef: PropTypes.string,
      passengerType: PropTypes.string,
      bucketCode: PropTypes.string,
      productFamily: PropTypes.shape({
        code: PropTypes.string
      }),
      currency: PropTypes.string,
      loyaltyPoints: PropTypes.number,
      price: PropTypes.number
    })
  ),
  reference: PropTypes.string,
  onSubmit: PropTypes.func,
  loading: PropTypes.bool,
  itemRefs: PropTypes.arrayOf(PropTypes.string),
  membershipNumber: PropTypes.string,
  additionalDetails: PropTypes.arrayOf(
    PropTypes.shape({
      itemRef: PropTypes.string,
      key: PropTypes.string,
      value: PropTypes.string
    })
  ),
  compensateLegs: PropTypes.arrayOf(
    PropTypes.shape({
      legName: PropTypes.string,
      products: PropTypes.arrayOf(
        PropTypes.shape({
          itemRef: PropTypes.string,
          passengerType: PropTypes.string,
          bucketCode: PropTypes.string,
          productFamily: PropTypes.shape({
            code: PropTypes.string
          }),
          currency: PropTypes.string,
          loyaltyPoints: PropTypes.number,
          price: PropTypes.number
        })
      )
    })
  ),
  compensationItems: compensationsShape,
  isCashTicketRefund: PropTypes.bool,
  currency: PropTypes.string,
  isLoyaltyBooking: PropTypes.bool
};

export default PrrRefundLoyalty;
